import fetchAccountBusiness from 'actions/businessTraveller/fetchBusinessAccount'
import useBusinessTravellerBusinessBudgetFetcher from 'businessTraveller/hooks/useBusinessTravellerBusinessBudgetFetcher'
import useItemPolicyEvaluation from 'businessTraveller/hooks/itemPolicy/useItemPolicyEvaluation'
import { buildItemPolicyEvaluationItem, hasRequiredItemEvaluationInputs } from 'businessTraveller/utils/buildItemPolicyEvaluationItem'
import type { ItemPolicyEvaluationItemParams } from 'businessTraveller/types/itemPolicyEvaluation.types'
import { generateItemPolicyHotelBudgetKey } from 'businessTraveller/utils/generateItemPolicyKeys'
import { selectHotelItemPolicyBudget } from 'businessTraveller/selectors/itemPolicy/selectHotelItemPolicyBudget'
import { selectSelectedTravellerEmployeeIds } from 'businessTraveller/selectors/itemPolicy/selectSelectedTravellerEmployeeIds'
import getBedbankPropertyIdFromSearchItem from 'businessTraveller/utils/getBedbankPropertyIdFromSearchItem'
import { getLocationDataFromOffer } from 'businessTraveller/utils/getLocationDataFromOffer'
import GeoContext from 'contexts/geoContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import { getDestinationIdFromURLSearchParams, getLandmarkIdFromURLSearchParams, getSearchItemFromURLSearchParams } from 'lib/url/searchUrlUtils'
import { getCenterCoordinates } from 'lib/search/searchUtils'
import { getOfferIdFromSearchItem } from 'components/Search/utils'
import { usePlaceByCoords } from 'hooks/usePlace'
import useOffer from 'hooks/Offers/useOffer'
import { useContext, useEffect, useMemo } from 'react'
import { useItemPolicyShadowFetcher } from 'businessTraveller/hooks/itemPolicy/useItemPolicyShadowFetcher'

interface Options {
  offerRequired?: boolean
  placeId?: string
  lat?: number
  lng?: number
  numberOfNights?: number
  roomCount?: number
  enableShadowCompare?: boolean
}

function useBusinessTravellerItemPolicyBudgetFetcher(offer?: App.AnyOffer, options: Options = {}) {
  const {
    offerRequired = false,
    placeId,
    lat,
    lng,
    numberOfNights,
    roomCount = 1,
    enableShadowCompare = true,
  } = options
  const { currentCurrency: currencyCode } = useContext(GeoContext)
  const appDispatch = useAppDispatch()
  const queryParams = useQueryParams()
  const employeeIds = useAppSelector(selectSelectedTravellerEmployeeIds)
  const businessId = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.business?.id)

  const {
    budgetData: legacyBudgetData,
    budgetKey: legacyBudgetKey,
    toBeCalculatedBusinessBudget: legacyBudgetParams,
  } = useBusinessTravellerBusinessBudgetFetcher(offer, {
    offerRequired,
    placeId,
    lat,
    lng,
  })

  const searchItem = useMemo(() => getSearchItemFromURLSearchParams(queryParams), [queryParams])
  const mapViewCoords = useMemo(() => {
    if (searchItem?.searchType === 'bounds') {
      const coords = searchItem.value?.split(',')
      if (coords && coords.length === 4) {
        const numCoords = coords.map(parseFloat)
        return getCenterCoordinates(numCoords as [number, number, number, number])
      }
    }
    return []
  }, [searchItem])
  const [place] = usePlaceByCoords(mapViewCoords[0], mapViewCoords[1])
  const lePlaceId = placeId ?? getDestinationIdFromURLSearchParams(queryParams) ?? getLandmarkIdFromURLSearchParams(queryParams) ?? place?.id
  const bedbankPropertyId = useMemo(() => getBedbankPropertyIdFromSearchItem(searchItem), [searchItem])
  const offerId = useMemo(() => {
    if (bedbankPropertyId || !searchItem) {
      return undefined
    }
    return getOfferIdFromSearchItem(searchItem)
  }, [bedbankPropertyId, searchItem])
  const [offerFromQueryParams] = useOffer(offerId)

  const { cacheKey, latitude, longitude } = useMemo(() => {
    const offerToUse = offer ?? offerFromQueryParams
    let resolvedLongitude =
      lng?.toString() ??
      (searchItem?.searchType === 'lat_long' ? searchItem.lng?.toString() : undefined) ??
      legacyBudgetParams.longitude
    let resolvedLatitude =
      lat?.toString() ??
      (searchItem?.searchType === 'lat_long' ? searchItem.lat?.toString() : undefined) ??
      legacyBudgetParams.latitude

    if (offerToUse) {
      const locationData = getLocationDataFromOffer(offerToUse)
      resolvedLongitude = locationData.location.longitude
      resolvedLatitude = locationData.location.latitude
    }

    const budgetKey = numberOfNights == null
      ? ''
      : generateItemPolicyHotelBudgetKey({
          currencyCode,
          longitude: resolvedLongitude,
          latitude: resolvedLatitude,
          lePlaceId,
          numberOfNights,
          roomCount,
          employeeIds,
        })

    return {
      cacheKey: budgetKey,
      latitude: resolvedLatitude,
      longitude: resolvedLongitude,
    }
  }, [currencyCode, employeeIds, lat, lePlaceId, legacyBudgetParams.latitude, legacyBudgetParams.longitude, lng, numberOfNights, offer, offerFromQueryParams, roomCount, searchItem])

  useEffect(() => {
    appDispatch(fetchAccountBusiness())
  }, [appDispatch])

  const budgetParams: ItemPolicyEvaluationItemParams = {
    itemPolicyType: 'HOTEL',
    businessId,
    cacheKey,
    currencyCode,
    numberOfNights: numberOfNights ?? 0,
    roomCount,
    employeeIds,
    lePlaceId,
    latitude,
    longitude,
  }

  const itemPolicyData = useItemPolicyEvaluation({
    enabled:
      numberOfNights != null &&
      (!offerRequired || !!offer || !!offerFromQueryParams) &&
      hasRequiredItemEvaluationInputs(budgetParams),
    businessId,
    cacheKey,
    item: buildItemPolicyEvaluationItem(budgetParams),
    employeeIds,
  })

  const budgetView = useAppSelector(state =>
    numberOfNights == null ? undefined : selectHotelItemPolicyBudget(state, { cacheKey, numberOfNights, roomCount }),
  )

  useItemPolicyShadowFetcher({
    enabled: enableShadowCompare && numberOfNights != null,
    businessId,
    cacheKey,
    item: undefined,
    employeeIds,
    vertical: 'hotel',
    surface: 'budget',
    legacyData: legacyBudgetData,
    itemPolicyData,
  })

  return {
    cacheKey,
    budgetView,
    itemPolicyData,
    legacyBudgetData,
    legacyBudgetKey,
    employeeIds,
  }
}

export default useBusinessTravellerItemPolicyBudgetFetcher
